import React, { useState, useEffect } from "react";
import DeviceLayout from "./DeviceLayout";

const DevicePdu = () => {
  const [liveData, setLiveData] = useState(null);
  const [displayData, setDisplayData] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState("");
  const [viewMode, setViewMode] = useState("live");
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const [historyError, setHistoryError] = useState(null);

  const getDeviceIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("id");
  };

  const fetchWithTimeout = async (url, options, timeout = 15000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: "include",
      });
      clearTimeout(id);
      return response;
    } catch (err) {
      clearTimeout(id);
      throw err;
    }
  };

  const fetchLatest = async () => {
    const id = getDeviceIdFromUrl();
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      setLiveError(null);

      const response = await fetchWithTimeout(`/api/pdu/${id}`, {}, 15000);
      if (!response.ok) {
        throw new Error(`Error fetching PDU data: ${response.statusText}`);
      }

      const data = await response.json();
      setLiveData(data);
      if (viewMode === "live") {
        setDisplayData(data);
      }
    } catch (err) {
      setLiveError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    const id = getDeviceIdFromUrl();
    if (!id) {
      return;
    }

    try {
      setHistoryLoading(true);
      setHistoryError(null);

      const response = await fetchWithTimeout(`/api/pdu/${id}/history`, {}, 15000);
      if (!response.ok) {
        throw new Error(`Error fetching history: ${response.statusText}`);
      }

      const data = await response.json();
      setHistoryItems(data);
      setSelectedHistoryId(data.length > 0 ? String(data[0].id) : "");
    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadHistoryItem = async (itemId) => {
    const deviceId = getDeviceIdFromUrl();
    if (!deviceId || !itemId) {
      return;
    }

    try {
      setLoading(true);
      setLiveError(null);

      const response = await fetchWithTimeout(`/api/pdu/${deviceId}/history/${itemId}`, {}, 15000);
      if (!response.ok) {
        throw new Error(`Error fetching saved PDU record: ${response.statusText}`);
      }

      const data = await response.json();
      setDisplayData(data);
      setViewMode("history");
      setSelectedHistoryId(String(itemId));
    } catch (err) {
      setLiveError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showLiveData = () => {
    setViewMode("live");
    setLiveError(null);
    setDisplayData(liveData);
  };

  // Intentionally run once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchLatest();
    fetchHistory();
  }, []);

  if (loading && !displayData && !liveData) {
    return <p>Loading PDU telemetry...</p>;
  }

  const currentData = viewMode === "history" ? displayData : (liveData || displayData);

  return (
    <DeviceLayout>
      <div>
        {liveError && (
          <p>
            Live PDU data could not be loaded: {liveError}. You can still browse saved records below.
          </p>
        )}

        <div style={{ marginBottom: "20px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button onClick={showLiveData} disabled={viewMode === "live"}>
            Live telemetry
          </button>
          <button
            onClick={() => {
              if (selectedHistoryId) {
                loadHistoryItem(selectedHistoryId);
              }
            }}
            disabled={historyLoading || !selectedHistoryId}
          >
            {historyLoading ? "Loading history..." : "Show saved record"}
          </button>
          <button onClick={fetchHistory} disabled={historyLoading}>
            Refresh history
          </button>
          <button onClick={fetchLatest}>
            Refresh live
          </button>
        </div>

        {historyError && <p>Error loading history: {historyError}</p>}

        {historyItems.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <label htmlFor="pdu-history-select">Saved PDU records</label>
            <div>
              <select
                id="pdu-history-select"
                value={selectedHistoryId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedHistoryId(nextId);
                  if (nextId) {
                    loadHistoryItem(nextId);
                  }
                }}
              >
                {historyItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {new Date(item.created_at).toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {viewMode === "history" && currentData && (
          <p>
            Showing saved PDU record #{currentData.id}
            {currentData.created_at ? ` from ${new Date(currentData.created_at).toLocaleString()}` : ""}
          </p>
        )}

        {currentData ? (
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(currentData).map(([key, value]) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>
                    {typeof value === "object" && value !== null
                      ? <pre>{JSON.stringify(value, null, 2)}</pre>
                      : String(value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No PDU telemetry data found.</p>
        )}
      </div>
    </DeviceLayout>
  );
};

export default DevicePdu;